/// <reference path="./global.d.ts" />

class PupmdPackageGalleryV2 extends HTMLElement {
  /** @type {InstanceType<typeof Swiper> | null} */
  #thumbSwiper = null;

  /** @type {InstanceType<typeof Swiper> | null} */
  #mainSwiper = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #initTimer = null;

  /** @type {number | null} */
  #layoutFrame = null;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    if (this.#initTimer !== null) {
      clearTimeout(this.#initTimer);
      this.#initTimer = null;
    }

    if (this.#layoutFrame !== null) {
      cancelAnimationFrame(this.#layoutFrame);
      this.#layoutFrame = null;
    }

    if (this.#mainSwiper) {
      this.#mainSwiper.destroy(true, true);
      this.#mainSwiper = null;
    }

    if (this.#thumbSwiper) {
      this.#thumbSwiper.destroy(true, true);
      this.#thumbSwiper = null;
    }
  }

  #init() {
    if (this.#mainSwiper) return;

    const thumbsElement = this.querySelector('[data-gallery-thumbs]');
    const mainElement = this.querySelector('[data-gallery-main]');

    if (!thumbsElement || !mainElement) return;

    if (typeof Swiper === 'undefined') {
      if (this.#initTimer !== null) return;

      this.#initTimer = setTimeout(() => {
        this.#initTimer = null;
        this.#init();
      }, 120);
      return;
    }

    if (this.#initTimer !== null) {
      clearTimeout(this.#initTimer);
      this.#initTimer = null;
    }

    const slideCount = mainElement.querySelectorAll('.swiper-slide').length;
    if (slideCount === 0) return;

    const isRTL = document.documentElement.dir === 'rtl';
    const enableLoop = slideCount > 1;
    const thumbsPerView = Math.min(5, slideCount);

    this.#thumbSwiper = new Swiper(thumbsElement, {
      spaceBetween: 8,
      slidesPerView: thumbsPerView,
      freeMode: true,
      watchSlidesProgress: true,
      speed: 500,
      rtl: isRTL,
      slideToClickedSlide: true,
      breakpoints: {
        0: {
          slidesPerView: thumbsPerView,
          spaceBetween: 8,
        },
        1021: {
          slidesPerView: thumbsPerView,
          spaceBetween: 8,
        },
      },
    });

    this.#mainSwiper = new Swiper(mainElement, {
      spaceBetween: 15,
      loop: enableLoop,
      speed: 700,
      rtl: isRTL,
      grabCursor: true,
      preloadImages: false,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      ...(enableLoop ? { loopedSlides: slideCount } : {}),
      thumbs: {
        swiper: this.#thumbSwiper,
      },
    });

    this.#bindThumbClicks(slideCount);
    this.#mainSwiper.on('slideChange', () => this.#syncThumbActiveState());
    this.#syncThumbActiveState();
    this.#bindImageLoad(mainElement);
    this.#bindImageLoad(thumbsElement);
    this.#scheduleLayoutUpdate();
  }

  #scheduleLayoutUpdate() {
    if (this.#layoutFrame !== null) {
      cancelAnimationFrame(this.#layoutFrame);
    }

    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = null;
      this.#mainSwiper?.update();
      this.#thumbSwiper?.update();
    });
  }

  /** @param {Element} rootElement */
  #bindImageLoad(rootElement) {
    rootElement.querySelectorAll('img').forEach((imageElement) => {
      if (!(imageElement instanceof HTMLImageElement)) return;
      if (imageElement.complete) return;

      const onImageReady = () => this.#scheduleLayoutUpdate();
      imageElement.addEventListener('load', onImageReady, { once: true });
      imageElement.addEventListener('error', onImageReady, { once: true });
    });
  }

  /** @param {number} slideCount */
  #bindThumbClicks(slideCount) {
    const thumbSwiper = this.#thumbSwiper;
    const mainSwiper = this.#mainSwiper;
    if (!thumbSwiper || !mainSwiper) return;

    thumbSwiper.on('click', (clickedSwiper) => {
      const clickedIndex = clickedSwiper.clickedIndex;
      if (typeof clickedIndex !== 'number' || clickedIndex < 0 || clickedIndex >= slideCount) {
        return;
      }

      if (slideCount > 1) {
        mainSwiper.slideToLoop(clickedIndex);
        return;
      }

      mainSwiper.slideTo(clickedIndex);
    });
  }

  #syncThumbActiveState() {
    const mainSwiper = this.#mainSwiper;
    const thumbSwiper = this.#thumbSwiper;
    if (!mainSwiper || !thumbSwiper) return;

    const activeIndex = mainSwiper.realIndex;

    thumbSwiper.slides.forEach((thumbSlide, thumbIndex) => {
      if (!(thumbSlide instanceof HTMLElement)) return;
      thumbSlide.classList.toggle('swiper-slide-thumb-active', thumbIndex === activeIndex);
    });
  }
}

if (!customElements.get('pupmd-package-gallery-v2')) {
  customElements.define('pupmd-package-gallery-v2', PupmdPackageGalleryV2);
}
